import { RingProgress } from '@mantine/core';
import {
  IconFiles,
  IconFolder,
  IconStar,
  IconShare,
  IconArrowRight,
  IconFileText,
  IconPhoto,
  IconVideo,
  IconFileZip,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, Group, Progress, SimpleGrid, Stack, Text, Title } from '@/components/ui';
import { fileStats, storageUsage, getRecentFiles, getFilesByParent, formatFileSize, FileItem } from '../mockData/files';

const StatCard = ({
  title,
  value,
  icon: Icon,
  color
}: {
  title: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
}) => (
  <Card>
    <Group justify="space-between" mb="xs">
      <Text size="sm" c="dimmed">{title}</Text>
      <Box
        style={{
          width: 40,
          height: 40,
          borderRadius: 8,
          backgroundColor: `var(--mantine-color-${color}-light)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={20} color={`var(--mantine-color-${color}-filled)`} />
      </Box>
    </Group>
    <Text size="xl" fw={700}>{value}</Text>
  </Card>
);

const getFileIcon = (type: FileItem['type']) => {
  switch (type) {
    case 'folder': return IconFolder;
    case 'image': return IconPhoto;
    case 'video': return IconVideo;
    case 'archive': return IconFileZip;
    default: return IconFileText;
  }
};

const getFileColor = (type: FileItem['type']) => {
  switch (type) {
    case 'folder': return 'blue';
    case 'image': return 'green';
    case 'video': return 'red';
    case 'pdf': return 'red';
    case 'document': return 'blue';
    case 'spreadsheet': return 'green';
    case 'presentation': return 'orange';
    case 'archive': return 'gray';
    case 'code': return 'violet';
    default: return 'gray';
  }
};

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
};

export default function FileDashboardPage() {
  const navigate = useNavigate();
  const recentFiles = getRecentFiles(5);
  const rootFolders = getFilesByParent(null);
  const usedPercentage = (storageUsage.used / storageUsage.total) * 100;

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>File Manager</Title>
          <Text c="dimmed" size="sm">Manage and organize your files</Text>
        </Box>
        <Group>
          <Button variant="light" onClick={() => navigate('shared')}>Shared Files</Button>
          <Button onClick={() => navigate('my-files')}>My Files</Button>
        </Group>
      </Group>

      {/* Stats Grid */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        <StatCard
          title="Total Files"
          value={fileStats.totalFiles}
          icon={IconFiles}
          color="blue"
        />
        <StatCard
          title="Folders"
          value={fileStats.totalFolders}
          icon={IconFolder}
          color="violet"
        />
        <StatCard
          title="Starred"
          value={fileStats.starredFiles}
          icon={IconStar}
          color="yellow"
        />
        <StatCard
          title="Shared"
          value={fileStats.sharedFiles}
          icon={IconShare}
          color="green"
        />
      </SimpleGrid>

      {/* Storage and Quick Access */}
      <SimpleGrid cols={{ base: 1, lg: 2 }}>
        {/* Storage Usage */}
        <Card>
          <Text fw={600} mb="md">Storage Usage</Text>
          <Group align="center" gap="xl">
            <RingProgress
              size={120}
              thickness={12}
              sections={[{ value: usedPercentage, color: 'blue' }]}
              label={
                <Box ta="center">
                  <Text size="lg" fw={700}>{usedPercentage.toFixed(1)}%</Text>
                  <Text size="xs" c="dimmed">Used</Text>
                </Box>
              }
            />
            <Box style={{ flex: 1 }}>
              <Group justify="space-between" mb="xs">
                <Text size="sm">Used</Text>
                <Text size="sm" fw={500}>{formatFileSize(storageUsage.used)}</Text>
              </Group>
              <Group justify="space-between" mb="xs">
                <Text size="sm">Available</Text>
                <Text size="sm" fw={500}>{formatFileSize(storageUsage.total - storageUsage.used)}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm">Total</Text>
                <Text size="sm" fw={500}>{formatFileSize(storageUsage.total)}</Text>
              </Group>
            </Box>
          </Group>
          <Stack gap="xs" mt="md">
            {storageUsage.byType.map(item => (
              <Group key={item.type} justify="space-between">
                <Text size="sm">{item.type}</Text>
                <Group gap="sm">
                  <Progress
                    value={(item.size / storageUsage.total) * 100}
                    size="sm"
                    radius="xl"
                    w={100}
                    color={item.color}
                  />
                  <Text size="xs" c="dimmed" w={70} ta="right">{formatFileSize(item.size)}</Text>
                </Group>
              </Group>
            ))}
          </Stack>
        </Card>

        {/* Quick Access Folders */}
        <Card>
          <Group justify="space-between" mb="md">
            <Text fw={600}>Quick Access</Text>
            <Button
              variant="subtle"
              size="xs"
              rightSection={<IconArrowRight size={14} />}
              onClick={() => navigate('my-files')}
            >
              View All
            </Button>
          </Group>
          <SimpleGrid cols={2}>
            {rootFolders.map(folder => (
              <Box
                key={folder.id}
                p="md"
                style={{
                  backgroundColor: 'var(--mantine-color-gray-light)',
                  borderRadius: 8,
                  cursor: 'pointer',
                }}
              >
                <Group gap="sm">
                  <Box
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      backgroundColor: 'var(--mantine-color-blue-light)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <IconFolder size={20} color="var(--mantine-color-blue-filled)" />
                  </Box>
                  <Box>
                    <Text size="sm" fw={500}>{folder.name}</Text>
                    <Text size="xs" c="dimmed">{formatDate(folder.modifiedAt)}</Text>
                  </Box>
                </Group>
              </Box>
            ))}
          </SimpleGrid>
        </Card>
      </SimpleGrid>

      {/* Recent Files */}
      <Card>
        <Group justify="space-between" mb="md">
          <Text fw={600}>Recent Files</Text>
          <Button
            variant="subtle"
            size="xs"
            rightSection={<IconArrowRight size={14} />}
            onClick={() => navigate('my-files')}
          >
            View All
          </Button>
        </Group>
        <Stack gap="sm">
          {recentFiles.map(file => {
            const Icon = getFileIcon(file.type);
            const color = getFileColor(file.type);
            return (
              <Group key={file.id} justify="space-between" p="sm" style={{ backgroundColor: 'var(--mantine-color-gray-light)', borderRadius: 8 }}>
                <Group gap="sm">
                  <Box
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      backgroundColor: `var(--mantine-color-${color}-light)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon size={20} color={`var(--mantine-color-${color}-filled)`} />
                  </Box>
                  <Box>
                    <Text size="sm" fw={500}>{file.name}</Text>
                    <Text size="xs" c="dimmed">{file.path}</Text>
                  </Box>
                </Group>
                <Group gap="md">
                  <Text size="xs" c="dimmed">{formatFileSize(file.size || 0)}</Text>
                  <Text size="xs" c="dimmed">{formatDate(file.modifiedAt)}</Text>
                </Group>
              </Group>
            );
          })}
        </Stack>
      </Card>
    </Stack>
  );
}
